import {
  readStoredItem,
  removeStoredItem,
  writeStoredItem,
  SESSION_SCOPE,
} from 'src/utils/storageManager';
import { isNative, getPlatform, isAndroid } from 'src/utils/platform';
import { SplashScreen as CapacitorSplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import SeasonalSplash from 'src/plugins/SeasonalSplashPlugin';
import { reportError } from 'src/modules/logs/Sentry';
import { withTimeout } from 'src/utils/withTimeout';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';

const RELOAD_COVER_SHOW_TIMEOUT_MS = 500;
const RELOAD_COVER_FALLBACK_MS = 10_000;
const RELOAD_COVER_LIFT_WAIT_MS = 3_000;
const HIDE_SETTLE_MS = 300;
const RELOAD_COVER_FADE_IN_MS = 100;
const HOLD_RELEASED_KEY = 'baobab_splash_hold_released';
const RELOAD_COVER_KEY = 'baobab_reload_cover';
const PREVIOUS_COVER_MAX_AGE_MS = 10 * 60_000;

const noop = () => {};

const SOURCES = [
  'gate_set',
  'gate_retry',
  'error_page',
  'environment_switch',
  'preview',
] as const;
export type ReloadCoverSource = (typeof SOURCES)[number];

export type ReloadCoverLiftReason =
  | 'fallback_guard'
  | 'operation_failed'
  | 'gate_stall'
  | 'gate_apply_grace'
  | 'gate_error';

const SHOW_OUTCOMES = [
  'pending',
  'shown',
  'timed_out',
  'rejected',
  'skipped_under_hold',
] as const;
type ReloadCoverShow = (typeof SHOW_OUTCOMES)[number];
type ReloadCoverBranch = 'seasonal' | 'capacitor';

type ReloadCoverRecord = {
  startedAt: number;
  show: ReloadCoverShow;
  source: ReloadCoverSource;
  branch: ReloadCoverBranch;
};

export type PreviousReloadCover = Omit<ReloadCoverRecord, 'startedAt'> & {
  ageMs: number;
};

type ActiveCover = { generation: number; record: ReloadCoverRecord };

const isShowOutcome = (value: unknown): value is ReloadCoverShow =>
  SHOW_OUTCOMES.some((outcome) => outcome === value);

const isSource = (value: unknown): value is ReloadCoverSource =>
  SOURCES.some((source) => source === value);

const parseReloadCoverRecord = (raw: string): ReloadCoverRecord | null => {
  const value: unknown = JSON.parse(raw);
  if (typeof value !== 'object' || value === null) return null;
  const { startedAt, show, source, branch } = value as Record<string, unknown>;
  if (
    typeof startedAt !== 'number' ||
    !isShowOutcome(show) ||
    !isSource(source) ||
    (branch !== 'seasonal' && branch !== 'capacitor')
  ) {
    return null;
  }
  return { startedAt, show, source, branch };
};

class SplashScreenManager {
  private useSeasonalSplash = false;
  private initialized = false;
  private cancelFallbackGuard: (() => void) | null = null;
  private pendingShow: Promise<void> | null = null;
  private lastHideAt = 0;
  private coverGeneration = 0;
  private holdReleasedRecorded = false;
  private cover: ActiveCover | null = null;
  // Read and cleared at construction, before this context can start a cover of its own.
  private previousCover: PreviousReloadCover | null = this.consumeStoredCover();

  initialize(): void {
    if (this.initialized) {
      return;
    }

    const platform = getPlatform();
    if (platform === 'ios' || platform === 'android') {
      this.useSeasonalSplash = Capacitor.isPluginAvailable(
        'SeasonalSplashPlugin',
      );
    }

    this.initialized = true;
  }

  // On Android the launch hold (keep-on-screen plus a content preDraw listener on the activity)
  // covers every WebView load until the first hide(), reloads included, and a plugin show() under
  // it can draw an overlay that the hold-release hide() never removes after an app pause. So on
  // Android the Capacitor show() only runs once a hide() has released the hold in this WebView.
  // iOS shows it over the hold harmlessly.
  async showAtBoot(): Promise<void> {
    if (!this.initialized) {
      this.initialize();
    }

    if (this.useSeasonalSplash) {
      await SeasonalSplash.show();
      return;
    }
    if (this.isLaunchHoldActive()) {
      return;
    }

    await CapacitorSplashScreen.show({ autoHide: false });
  }

  private isLaunchHoldActive(): boolean {
    if (this.useSeasonalSplash || !isAndroid()) {
      return false;
    }
    if (this.holdReleasedRecorded) {
      return false;
    }
    return readStoredItem(HOLD_RELEASED_KEY, SESSION_SCOPE) === null;
  }

  private recordHoldReleased(): void {
    if (this.holdReleasedRecorded) {
      return;
    }
    this.holdReleasedRecorded = true;
    writeStoredItem(HOLD_RELEASED_KEY, '1', SESSION_SCOPE);
  }

  // The cover is a native view outside the WebView, so it survives the reload and the next
  // bundle's first-paint hide() removes it. Capgo's set() reloads the WebView itself, so wrap it.
  async withReloadCover<T>(
    operation: () => Promise<T>,
    source: ReloadCoverSource,
  ): Promise<T> {
    if (!isNative()) {
      return operation();
    }
    if (!this.initialized) {
      this.initialize();
    }

    this.coverGeneration += 1;
    const generation = this.coverGeneration;
    this.beginCoverRecord(source);
    if (this.isLaunchHoldActive()) {
      this.setCoverShow(generation, 'skipped_under_hold');
    } else {
      await this.waitForHideToSettle();
      await this.showCoverBounded(generation);
    }
    this.armFallbackGuard(generation);
    try {
      return await operation();
    } catch (error) {
      void this.liftReloadCover('operation_failed', generation);
      throw error;
    }
  }

  async reloadPageWithCover(source: ReloadCoverSource): Promise<void> {
    await this.withReloadCover(async () => {
      // eslint-disable-next-line no-restricted-syntax -- the covered reload helper itself
      window.location.reload();
    }, source);
  }

  // The cover that preceded this boot, once: the reload destroyed the context that knew its outcome.
  takePreviousReloadCover(): PreviousReloadCover | null {
    const previous = this.previousCover;
    this.previousCover = null;
    return previous;
  }

  private consumeStoredCover(): PreviousReloadCover | null {
    try {
      const raw = readStoredItem(RELOAD_COVER_KEY, SESSION_SCOPE);
      if (raw === null) return null;
      removeStoredItem(RELOAD_COVER_KEY, SESSION_SCOPE);
      const record = parseReloadCoverRecord(raw);
      if (!record) return null;
      const { startedAt, ...rest } = record;
      const ageMs = Math.max(0, Date.now() - startedAt);
      return ageMs > PREVIOUS_COVER_MAX_AGE_MS ? null : { ...rest, ageMs };
    } catch {
      return null;
    }
  }

  private beginCoverRecord(source: ReloadCoverSource): void {
    this.cover = {
      generation: this.coverGeneration,
      record: {
        startedAt: Date.now(),
        show: 'pending',
        source,
        branch: this.useSeasonalSplash ? 'seasonal' : 'capacitor',
      },
    };
    this.persistCover();
  }

  private setCoverShow(generation: number, show: ReloadCoverShow): void {
    if (this.cover?.generation !== generation) return;
    this.cover.record.show = show;
    this.persistCover();
  }

  private persistCover(): void {
    writeStoredItem(RELOAD_COVER_KEY, JSON.stringify(this.cover?.record), SESSION_SCOPE);
  }

  // Only the cover's own lift reports: a stale guard or an earlier operation settling during a
  // newer cover must neither emit nor erase the newer record. A reload that is merely slow may
  // still land, so those two lifts keep the record for the next boot to carry.
  private reportCoverFailure(
    reason: ReloadCoverLiftReason,
    ownerGeneration: number,
  ): void {
    const cover = this.cover;
    if (cover?.generation !== ownerGeneration) return;
    this.cover = null;
    if (reason !== 'fallback_guard' && reason !== 'gate_apply_grace') {
      removeStoredItem(RELOAD_COVER_KEY, SESSION_SCOPE);
    }
    try {
      Promise.resolve(
        logAnalyticsEvent('reload_cover_failed', {
          failureReason: reason,
          durationMs: Math.max(0, Date.now() - cover.record.startedAt),
          reloadCoverSource: cover.record.source,
          reloadCoverBranch: cover.record.branch,
          reloadCoverShow: cover.record.show,
        }),
      ).catch(noop);
    } catch {
      // Analytics must never block lifting the cover.
    }
  }

  // A cover shown inside the plugin's hide transition is torn down by that transition's end.
  private async waitForHideToSettle(): Promise<void> {
    const remaining = HIDE_SETTLE_MS - (Date.now() - this.lastHideAt);
    if (remaining > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, remaining));
    }
  }

  private async showCoverBounded(generation: number): Promise<void> {
    const show = this.showCover();
    let rejected = false;
    const settled = show.then(noop, () => {
      rejected = true;
    });
    this.pendingShow = settled;
    void settled.then(() => {
      if (this.pendingShow === settled) this.pendingShow = null;
    });
    try {
      await withTimeout(show, RELOAD_COVER_SHOW_TIMEOUT_MS, 'Reload cover show');
      this.setCoverShow(generation, 'shown');
    } catch (error) {
      this.setCoverShow(generation, rejected ? 'rejected' : 'timed_out');
      reportError('[SplashScreen] Reload cover not shown in time:', error);
    }
  }

  private async showCover(): Promise<void> {
    if (this.useSeasonalSplash) {
      await SeasonalSplash.show();
      return;
    }

    await CapacitorSplashScreen.show({
      autoHide: false,
      fadeInDuration: RELOAD_COVER_FADE_IN_MS,
    });
  }

  // Armed for every cover, gate callers included: a reload that took effect kills this JS context
  // first, and the gate lifts earlier on its own failure signals.
  private armFallbackGuard(generation: number): void {
    this.cancelFallbackGuard?.();
    const timer = setTimeout(
      () => void this.liftReloadCover('fallback_guard', generation),
      RELOAD_COVER_FALLBACK_MS,
    );
    this.cancelFallbackGuard = () => clearTimeout(timer);
  }

  // A show() that lost its bound is still running natively, and hide() ignores a splash that is
  // not fully up yet, so wait for it before hiding.
  async liftReloadCover(
    reason: ReloadCoverLiftReason,
    ownerGeneration?: number,
  ): Promise<void> {
    const generation = this.coverGeneration;
    this.cancelFallbackGuard?.();
    this.cancelFallbackGuard = null;
    this.reportCoverFailure(reason, ownerGeneration ?? generation);
    if (this.pendingShow) {
      await withTimeout(
        this.pendingShow,
        RELOAD_COVER_LIFT_WAIT_MS,
        'Pending reload cover show',
      ).catch(noop);
    }
    if (generation !== this.coverGeneration) {
      return;
    }
    await this.hide().catch(noop);
  }

  async hide(): Promise<void> {
    if (!this.initialized) {
      this.initialize();
    }

    this.lastHideAt = Date.now();
    this.recordHoldReleased();
    try {
      if (this.useSeasonalSplash) {
        await SeasonalSplash.hide();
      } else {
        await CapacitorSplashScreen.hide();
      }
    } catch (error) {
      reportError('[SplashScreen] Error hiding splash:', error);
      await CapacitorSplashScreen.hide();
    }
  }

  isUsingSeasonalSplash(): boolean {
    return this.useSeasonalSplash;
  }
}

export const SplashScreen = new SplashScreenManager();

export default SplashScreen;
